// @flow

import React, { type Element, useState, useContext } from 'react'
import { Typography, Grid, Box, Modal, Button, CircularProgress } from '@material-ui/core'
import CloseIcon from '@material-ui/icons/Close'
import { getCsrfToken, request } from '../../../Common/Request'
import { UserContext } from '../../../Common/UserContext'
import type { PrivateFacility } from '../../types'

type Props = {|
  handleServerErrors: ({ [string]: Array<string> }) => void,
  modalOpen: boolean,
  closeModal: () => void,
  createdFacilities: Array<PrivateFacility>,
  setCreatedFacilities: (any) => void,
  facilityId: string,
  showSuccess: () => void,
|}

function PrivateFacilityDeleteModal({
  handleServerErrors,
  modalOpen,
  closeModal,
  createdFacilities,
  setCreatedFacilities,
  facilityId,
  showSuccess,
}: Props): Element<typeof React.Fragment> {
  const [isLoading, setIsLoading] = useState(false)
  const userContext = useContext(UserContext)

  const removeFacility = () => {
    const indexToRemove = createdFacilities.findIndex((facility) => {
      return facility.id === facilityId
    })
    const updatedFacilities = createdFacilities
    updatedFacilities.splice(indexToRemove, 1)
    setCreatedFacilities(updatedFacilities)
  }

  const handleSuccess = () => {
    removeFacility()
    showSuccess()
    closeModal()
  }

  function handleError(errors: { [string]: Array<string> }): void {
    handleServerErrors(errors)
    closeModal()
  }

  const deleteFacility = async () => {
    setIsLoading(true)

    const path = userContext.deleteFacilityPath(facilityId)
    const headers = { 'X-CSRF-Token': getCsrfToken() }

    await request(path, 'DELETE', headers, '', handleError, handleSuccess)

    setIsLoading(false)
  }

  return (
    <Modal open={modalOpen}>
      <Box
        style={{ outline: 0 }}
        borderRadius={8}
        padding={3.5}
        bgcolor="white"
        position="relative"
        width="50%"
        minWidth={350}
        maxWidth={500}
        m="auto"
        top="25%"
      >
        <Grid container direction="row" justify="flex-end" alignItems="center">
          <CloseIcon onClick={closeModal} style={{ cursor: 'pointer' }} />
        </Grid>
        <Grid container>
          <Grid item xs={12}>
            <Box mb={2}>
              <Typography variant="h5">Are you sure you want to delete this facility?</Typography>
            </Box>
          </Grid>
          <Grid item xs={12}>
            <Box mb={3}>
              <Box mt={1}>
                <Typography>
                  This will not remove this facility from any pre-existing claims.
                </Typography>
              </Box>
              <Box mt={1}>
                <Typography>
                  If this facility is your default facility, this will remove the facility as the
                  default facility on your profile as well.
                </Typography>
              </Box>
            </Box>
          </Grid>
          <Grid container direction="row" justify="flex-end" alignItems="center">
            <Button onClick={closeModal} color="primary">
              Cancel
            </Button>
            <Box ml={3} display="inline-block" position="relative">
              <Button
                variant="contained"
                color="primary"
                onClick={deleteFacility}
                disabled={isLoading}
              >
                Delete
              </Button>
              {isLoading && (
                <CircularProgress
                  size={24}
                  style={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    marginTop: -12,
                    marginLeft: -12,
                  }}
                />
              )}
            </Box>
          </Grid>
        </Grid>
      </Box>
    </Modal>
  )
}

export default PrivateFacilityDeleteModal
